import type { FC } from 'react';
import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkItem } from '@atlaskit/side-navigation';
import Tooltip from '@atlaskit/tooltip';
import { usePageLayoutResize } from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import AutomationIcon from '@atlaskit/icon/core/automation';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useIsElevatedAutomationUpsellEnabled } from '@confluence/change-edition/entry-points/useIsElevateAutomationEnabled';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import {
	AUTOMATION_SIDE_NAV_BAR_EXPERIENCE,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { AutomationChangeBoardingLoader } from '@confluence/manage-pages/entry-points/AutomationChangeboardingLoader';
import { SPACE_SETTINGS_AUTOMATION } from '@confluence/named-routes';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from '@confluence/premium-onboarding/entry-points/useIsPremiumOnboardingFeatureDiscoveryEnabled';
import {
	useTrackPremiumFeatureUsage,
	PremiumFeatureUsage,
} from '@confluence/premium-onboarding/entry-points/useTrackPremiumFeatureUsage';
import { PremiumLozengeLoadable } from '@confluence/trial-components';

import { AutomationIcon as AutomationLegacyIcon } from './img/AutomationIcon';
import { cssFn } from './LegacySpaceLinks';

const listStyles = xcss({
	marginTop: 'space.0',
});

const i18n = defineMessages({
	automationTooltip: {
		id: 'side-navigation.space-navigation.automation.tooltip',
		defaultMessage: 'View automation for this space',
		description: 'Tooltip message on hover of automation space link',
	},
});

type AutomationNavigationProps = {
	onClick?: () => void;
	isSpaceAdmin?: boolean;
	isSelected: boolean;
};

const AutomationNavigationComponent: FC<AutomationNavigationProps> = ({
	onClick,
	isSpaceAdmin = false,
	isSelected,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const intl = useIntl();
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const isUpsellEnabled = useIsElevatedAutomationUpsellEnabled({
		isSpaceAdmin,
	});

	const hasSeenSmartButtonChangeboarding = localStorage.getItemAsBoolean(
		'dismissSmartButtonSpaceNavigationChangeBoarding',
	);
	const shouldDisplayChangeboarding =
		!hasSeenSmartButtonChangeboarding && isSpaceAdmin && fg('cc_a4c_smart_button_changeboarding');
	const { isLeftSidebarCollapsed } = usePageLayoutResize();

	// Please also update getPremiumFeaturesWebItemKeys() in SpaceNavigation if this condition is updated
	const isEnabled = edition === ConfluenceEdition.PREMIUM || isUpsellEnabled;
	// This FG should override edition checks and upsells when enabled

	useEffect(() => {
		if (isEnabled || fg('a4c_free_and_standard_editions')) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'automationLink',
					attributes: {
						edition,
						source: 'sideNavBar',
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, edition, isEnabled]);

	const { isPremiumOnboardingFeatureDiscoveryEnabled } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled();

	// mark automation as being used if the current url hash matches the rule created hash
	const RULE_IS_CREATED_URL_HASH_REGEX = /^#\/rule\/(\d+)\/__RULE_TURNED_ON$/;
	const RULE_IS_NEW_URL_HASH_REGEX = /^#\/rule\/new$/;
	const shouldTrackPremiumFeature =
		// stop tracking if we're enabling for all editions
		!fg('a4c_free_and_standard_editions') &&
		isSelected &&
		(RULE_IS_CREATED_URL_HASH_REGEX.test(location?.hash) ||
			RULE_IS_NEW_URL_HASH_REGEX.test(location?.hash));

	const { markFeatureAsUsed } = useTrackPremiumFeatureUsage(
		PremiumFeatureUsage.AUTOMATION_NEW_RULE,
		/* skip */ !shouldTrackPremiumFeature,
	);

	useEffect(markFeatureAsUsed, [markFeatureAsUsed]);

	if (!isEnabled && !fg('a4c_free_and_standard_editions')) return null;

	const automationNavLink = () => (
		<Tooltip position="top" content={intl.formatMessage(i18n.automationTooltip)}>
			{(tooltipProps) => (
				<Box as="li" xcss={listStyles}>
					<LinkItem
						// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
						cssFn={cssFn}
						href={SPACE_SETTINGS_AUTOMATION.toUrl({ spaceKey }, { hash: '#/tab/rule-library' })}
						iconBefore={
							<AutomationIcon
								spacing="spacious"
								label=""
								color={
									isSelected
										? token('color.icon.selected', '#0c66e4')
										: token('color.icon', '#42526E')
								}
								LEGACY_fallbackIcon={AutomationLegacyIcon}
							/>
						}
						isSelected={isSelected}
						{...tooltipProps}
						onClick={onClick}
						testId="space-navigation-automation-link"
						data-vc="automation-link"
						{...ssrPlaceholderIdProp}
					>
						<FormattedMessage
							id="side-navigation.container.automation"
							defaultMessage="Automation"
							description="Automation Menu Item"
						/>
						{!isUpsellEnabled &&
							isPremiumOnboardingFeatureDiscoveryEnabled &&
							!fg('a4c_free_and_standard_editions') && <PremiumLozengeLoadable />}
						<ExperienceSuccess name={AUTOMATION_SIDE_NAV_BAR_EXPERIENCE} />
					</LinkItem>
				</Box>
			)}
		</Tooltip>
	);

	return (
		<>
			<ExperienceStart name={AUTOMATION_SIDE_NAV_BAR_EXPERIENCE} />
			{shouldDisplayChangeboarding && !isLeftSidebarCollapsed ? (
				<AutomationChangeBoardingLoader spaceSideNavLinkTarget={automationNavLink()} />
			) : (
				automationNavLink()
			)}
		</>
	);
};

export const AutomationNavigation = (props: AutomationNavigationProps) => (
	<ErrorBoundary attribution={Attribution.AUTOMATION_FOR_CONFLUENCE}>
		<AutomationNavigationComponent {...props} />
	</ErrorBoundary>
);
